import BillingsOrderRailyTaxiRecalculateResponse, {
  BillingsOrderRailyTaxiRecalculateResponseDataBillingNode,
  BillingsOrderRailyTaxiRecalculateResponseDataCargoOrder,
  BillingsOrderRailyTaxiRecalculateResponseDataContractBillingModel,
  BillingsOrderRailyTaxiRecalculateResponseDataContractTaxiCompany,
  BillingsOrderRailyTaxiRecalculateResponseDataContract,
  BillingsOrderRailyTaxiRecalculateResponseDataBonusType,
  BillingsOrderRailyTaxiRecalculateResponseDataPenaltyType,
  BillingsOrderRailyTaxiRecalculateResponseData,
} from "../../../../../api/raily/billings/order/raily-taxi/recalculate/billings-order-raily-taxi-recalculate.response";
import BillingModel from "../../../../../types/billing-model";
import BillingsOrderRailyTaxiRecalculate, {
  BillingsOrderRailyTaxiRecalculateBillingNode,
  BillingsOrderRailyTaxiRecalculateBonusType,
  BillingsOrderRailyTaxiRecalculateCargoOrder,
  BillingsOrderRailyTaxiRecalculateContract,
  BillingsOrderRailyTaxiRecalculateContractTaxiCompany,
  BillingsOrderRailyTaxiRecalculatePenaltyType,
} from "./billings-order-raily-taxi-recalculate";

const createBillingNode = (
  node: BillingsOrderRailyTaxiRecalculateResponseDataBillingNode,
): BillingsOrderRailyTaxiRecalculateBillingNode => {
  return {
    allowCharge: node.allowCharge,
    description: node.description,
    distance: node.distance,
    editableCoordinates: node.editableCoordinates,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    lat: node.lat,
    lon: node.lon,
    nodeUuid: node.nodeId,
    plannedDistance: node.plannedDistance,
    uuid: node.id,
    checkoutDate: node.checkoutDate,
    checkoutEventUuid: node.checkoutEventId,
    orderUuid: node.orderId,
    plannedDate: node.plannedDate,
  };
};

const createBillingNodes = (
  nodes: BillingsOrderRailyTaxiRecalculateResponseDataBillingNode[],
): BillingsOrderRailyTaxiRecalculateBillingNode[] => {
  return nodes.map(createBillingNode);
};

const createCargoOrder = (
  cargoOrder: BillingsOrderRailyTaxiRecalculateResponseDataCargoOrder,
): BillingsOrderRailyTaxiRecalculateCargoOrder => {
  return {
    humanId: cargoOrder.humanId,
  };
};

const createModel = (
  model: BillingsOrderRailyTaxiRecalculateResponseDataContractBillingModel,
): BillingModel => {
  switch (model) {
    case BillingsOrderRailyTaxiRecalculateResponseDataContractBillingModel.AB:
      return BillingModel.AB;
    case BillingsOrderRailyTaxiRecalculateResponseDataContractBillingModel.ABA:
      return BillingModel.ABA;
    case BillingsOrderRailyTaxiRecalculateResponseDataContractBillingModel.OTHER:
      return BillingModel.OTHER;
    case BillingsOrderRailyTaxiRecalculateResponseDataContractBillingModel.SABS:
      return BillingModel.SABS;
  }
};

const createTaxiCorporation = (
  taxiCorporation: BillingsOrderRailyTaxiRecalculateResponseDataContractTaxiCompany,
): BillingsOrderRailyTaxiRecalculateContractTaxiCompany => {
  return {
    companyName: taxiCorporation.companyName,
  };
};

const createContract = (
  contract: BillingsOrderRailyTaxiRecalculateResponseDataContract,
): BillingsOrderRailyTaxiRecalculateContract => {
  return {
    distanceRate: contract.distanceRate,
    haltingTimeAfterMinutes: contract.haltingTimeAfterMinutes,
    haltingTimeRate: contract.haltingTimeRate,
    model: createModel(contract.model),
    taxiCorporation: createTaxiCorporation(contract.taxiCorporation),
  };
};

const createBonusType = (
  bonusType: BillingsOrderRailyTaxiRecalculateResponseDataBonusType | undefined,
): BillingsOrderRailyTaxiRecalculateBonusType | undefined => {
  switch (bonusType) {
    case BillingsOrderRailyTaxiRecalculateResponseDataBonusType.DISTANCE_RATE_CHANGED:
      return BillingsOrderRailyTaxiRecalculateBonusType.DISTANCE_RATE_CHANGED;
    case BillingsOrderRailyTaxiRecalculateResponseDataBonusType.HALTING_RATE_CHANGED:
      return BillingsOrderRailyTaxiRecalculateBonusType.HALTING_RATE_CHANGED;
    case BillingsOrderRailyTaxiRecalculateResponseDataBonusType.OTHER:
      return BillingsOrderRailyTaxiRecalculateBonusType.OTHER;
    case BillingsOrderRailyTaxiRecalculateResponseDataBonusType.SHORT_ROUTE:
      return BillingsOrderRailyTaxiRecalculateBonusType.SHORT_ROUTE;
  }
};

const createPenaltyType = (
  penaltyType:
    | BillingsOrderRailyTaxiRecalculateResponseDataPenaltyType
    | undefined,
): BillingsOrderRailyTaxiRecalculatePenaltyType | undefined => {
  switch (penaltyType) {
    case BillingsOrderRailyTaxiRecalculateResponseDataPenaltyType.BEING_LATE:
      return BillingsOrderRailyTaxiRecalculatePenaltyType.BEING_LATE;
    case BillingsOrderRailyTaxiRecalculateResponseDataPenaltyType.GUIDELINE_IGNORED:
      return BillingsOrderRailyTaxiRecalculatePenaltyType.GUIDELINE_IGNORED;
    case BillingsOrderRailyTaxiRecalculateResponseDataPenaltyType.HIGHWAY_NOT_ALLOWED:
      return BillingsOrderRailyTaxiRecalculatePenaltyType.HIGHWAY_NOT_ALLOWED;
    case BillingsOrderRailyTaxiRecalculateResponseDataPenaltyType.INCOMPATIBLE_CAR:
      return BillingsOrderRailyTaxiRecalculatePenaltyType.INCOMPATIBLE_CAR;
    case BillingsOrderRailyTaxiRecalculateResponseDataPenaltyType.OPTIMIZATION_REFUSED:
      return BillingsOrderRailyTaxiRecalculatePenaltyType.OPTIMIZATION_REFUSED;
    case BillingsOrderRailyTaxiRecalculateResponseDataPenaltyType.OTHER:
      return BillingsOrderRailyTaxiRecalculatePenaltyType.OTHER;
    case BillingsOrderRailyTaxiRecalculateResponseDataPenaltyType.SUBOPTIMAL_RIDE:
      return BillingsOrderRailyTaxiRecalculatePenaltyType.SUBOPTIMAL_RIDE;
    default:
      return undefined;
  }
};

const createData = (
  data: BillingsOrderRailyTaxiRecalculateResponseData,
): BillingsOrderRailyTaxiRecalculate => {
  return {
    billingNodes: createBillingNodes(data.billingNodes),
    bonus: data.bonus,
    cargoOrder: createCargoOrder(data.cargoOrder),
    contract: createContract(data.contract),
    date: data.date,
    distance: data.distance,
    distanceAmount: data.distanceAmount,
    haltingAmount: data.haltingAmount,
    highwayCharge: data.highwayCharge,
    penalty: data.penalty,
    planEntryUuid: data.planEntryId,
    totalAmount: data.totalAmount,
    bonusType: createBonusType(data.bonusType),
    penaltyType: createPenaltyType(data.penaltyType),
  };
};

const create = (
  response: BillingsOrderRailyTaxiRecalculateResponse,
): BillingsOrderRailyTaxiRecalculate => {
  return createData(response.data);
};

const billingsOrderRailyTaxiRecalculateFactory = {
  create,
};

export default billingsOrderRailyTaxiRecalculateFactory;
